import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { AIAssistant } from '@/components/shared/AIAssistant';
import type { EntityConfig } from '@/lib/entity-config';
import type { SidebarSection } from './Sidebar';
import { cn } from '@/lib/cn';

interface EntityLayoutProps {
  entity: EntityConfig;
  headerActions?: React.ReactNode;
  sidebarSections?: SidebarSection[];
  aiTitle?: string;
  aiInitialMessage?: string;
  aiPlaceholder?: string;
  children: React.ReactNode;
  onClose?: () => void;
}

export function EntityLayout({
  entity,
  headerActions,
  sidebarSections = [],
  aiTitle,
  aiInitialMessage,
  aiPlaceholder,
  children,
  onClose,
}: EntityLayoutProps) {
  // Determine layout based on entity configuration
  const layoutClass = getLayoutClass(entity.layoutMode);
  const showSidebar = sidebarSections.length > 0 && entity.layoutMode === 'sidebar-canvas-panel';
  const showAI = entity.hasAI;

  return (
    <div className="flex h-screen flex-col bg-ck-dark">
      {/* Header */}
      <Header entity={entity} actions={headerActions} onClose={onClose} />

      {/* Main Content Area */}
      <div className={cn('flex flex-1', layoutClass)}>
        {/* Left Sidebar (conditional) */}
        {showSidebar && (
          <Sidebar sections={sidebarSections} className="w-80 overflow-y-auto" />
        )}

        {/* Center Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

        {/* Right Panel - AI Assistant (conditional) */}
        {showAI && (
          <div className="w-96 border-l border-ck-dark-border bg-ck-dark overflow-y-auto">
            <AIAssistant
              title={aiTitle}
              initialMessage={aiInitialMessage}
              placeholder={aiPlaceholder}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function getLayoutClass(mode: EntityConfig['layoutMode']): string {
  switch (mode) {
    case 'sidebar-canvas-panel':
      return 'grid grid-cols-[320px_1fr_384px] overflow-hidden';
    case 'split-view':
      return 'grid grid-cols-2 overflow-hidden';
    case 'gallery-view':
      return 'flex-col overflow-hidden';
    default:
      return 'overflow-hidden';
  }
}

export type { SidebarSection } from './Sidebar';